import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import { useIsAdmin, useMyInstructing } from '@/lib/queries';
import { Emblem } from '@/components/Brand';
import { GlobalSearch } from '@/components/GlobalSearch';

const nav = ({ isActive }: { isActive: boolean }) =>
  `border-b-2 px-2 py-2 text-[15px] font-semibold ${isActive ? 'border-brand-600 text-ink-900' : 'border-transparent text-ink-500 hover:text-ink-900'}`;

export function Layout() {
  const { session } = useAuth();
  const admin = useIsAdmin();
  const instructing = useMyInstructing();
  const navigate = useNavigate();
  const signOut = async () => { await supabase.auth.signOut(); navigate('/'); };
  return (
    <div className="flex min-h-screen flex-col">
      {import.meta.env.VITE_ENVIRONMENT !== 'production' && (
        <div className="bg-ink-900 px-4 py-1.5 text-center text-xs font-semibold tracking-wide text-paper">
          Test site: no real payments are taken and the data is not real.
        </div>
      )}
      <header className="border-b border-stone-200 bg-white">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-2 px-4 py-3">
          <Link to="/" className="flex items-center gap-2.5">
            <Emblem size={42} />
            <span className="leading-tight">
              <span className="block font-display text-lg font-bold tracking-wide">EGKA Members</span>
              <span className="block text-xs text-ink-500">English Goju-Ryu Karate-do Association</span>
            </span>
          </Link>
          {/* On a phone, sign out sits beside the logo so the menu stays on one line. */}
          {session && (
            <button className="btn-ghost px-2.5 sm:hidden" title="Sign out" aria-label="Sign out" onClick={signOut}>
              <LogOut size={16} />
            </button>
          )}
          {session && <GlobalSearch />}
          <nav className="flex flex-wrap items-center gap-1">
            <NavLink to="/events" className={nav}>Events</NavLink>
            <NavLink to="/dojos" className={nav}>Dojos</NavLink>
            {session ? (
              <>
                <NavLink to="/home" className={nav}>My household</NavLink>
                {!!instructing.data?.length && <NavLink to="/dojo" className={nav}>My dojo</NavLink>}
                {admin.data && <NavLink to="/admin" className={nav}>Admin</NavLink>}
                <button className="btn-ghost hidden px-2.5 sm:inline-flex" title="Sign out" aria-label="Sign out" onClick={signOut}>
                  <LogOut size={16} />
                </button>
              </>
            ) : (
              <NavLink to="/signin" className="btn-primary ml-1 py-2">Sign in</NavLink>
            )}
          </nav>
        </div>
      </header>
      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-8"><Outlet /></main>
      <footer className="border-t border-stone-200 bg-white py-8 text-center text-sm text-ink-500">
        Questions about your membership? <a className="underline" href="mailto:membership.secretary@egka.org.uk">membership.secretary@egka.org.uk</a>
      </footer>
    </div>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return <p className="py-12 text-center text-sm text-ink-500" role="status">{label}…</p>;
}

export function ErrorNote({ children }: { children: React.ReactNode }) {
  return <div className="rounded-sm border border-brand-200 bg-brand-50 px-4 py-3 text-[15px] text-brand-800" role="alert">{children}</div>;
}
